import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { Trash2, Plus, RefreshCw, Edit2 } from "lucide-react";

const EMPTY = { name: "", url: "", source_type: "rss", region: "Global", reliability: 7, fetch_frequency_hours: 3, copyright_notes: "", active: true, topics: [] };

export default function Sources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [ingesting, setIngesting] = useState(null);

  const load = () => api.get("/sources").then((r) => setItems(r.data));
  useEffect(() => { document.title = "Sources — IndiaODR Admin"; load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editingId) { await api.patch(`/sources/${editingId}`, form); toast.success("Source updated."); }
      else { await api.post("/sources", form); toast.success("Source added."); }
      setForm(EMPTY); setEditingId(null); load();
    } catch { toast.error("Save failed."); }
    finally { setLoading(false); }
  };
  const remove = async (id) => { if (!confirm("Delete this source?")) return; try { await api.delete(`/sources/${id}`); toast.success("Deleted."); load(); } catch { toast.error("Failed."); }};
  const fetchNow = async (id) => { setIngesting(id); try { const { data } = await api.post(`/ingest/source/${id}`); toast.success(`Ingested: ${data.stored} new, ${data.duplicates} duplicate.`); } catch { toast.error("Fetch failed."); } finally { setIngesting(null); }};
  const toggleActive = async (s) => { await api.patch(`/sources/${s.id}`, { active: !s.active }); load(); };
  const edit = (s) => { setEditingId(s.id); setForm({ ...EMPTY, ...s }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const input = "border border-[var(--io-border)] focus:border-[var(--io-gold)] bg-[var(--io-bg-2)] px-3 py-2 rounded-md text-[13px] text-[var(--io-text)] placeholder:text-[var(--io-muted)] outline-none";

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Source Manager</div>
          <h1 className="font-serif text-[32px] md:text-[40px] mt-1 text-[var(--io-text)]">Intelligence sources</h1>
          <p className="text-[12px] font-mono text-[var(--io-muted)] mt-1">{items.length} sources · {items.filter(i => i.active).length} active</p>
        </div>
      </div>

      <form onSubmit={submit} className="io-card-el p-5 mb-8 grid md:grid-cols-6 gap-3" data-testid="source-form">
        <input placeholder="Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`md:col-span-2 ${input}`} data-testid="source-name-input"/>
        <input placeholder="URL *" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={`md:col-span-3 ${input}`} data-testid="source-url-input"/>
        <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} className={input} data-testid="source-type-input">
          <option value="rss">RSS</option><option value="institution">Institution</option><option value="law_firm">Law firm</option>
          <option value="court">Court</option><option value="manual">Manual</option><option value="news_api">News API</option>
        </select>
        <input placeholder="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={input} data-testid="source-region-input"/>
        <input type="number" min="1" max="10" placeholder="Reliability 1-10" value={form.reliability} onChange={(e) => setForm({ ...form, reliability: +e.target.value })} className={input} data-testid="source-reliability-input"/>
        <input type="number" min="1" max="48" placeholder="Freq hrs" value={form.fetch_frequency_hours} onChange={(e) => setForm({ ...form, fetch_frequency_hours: +e.target.value })} className={input} data-testid="source-freq-input"/>
        <input placeholder="Copyright notes" value={form.copyright_notes || ""} onChange={(e) => setForm({ ...form, copyright_notes: e.target.value })} className={`md:col-span-3 ${input}`} data-testid="source-copyright-input"/>
        <div className="md:col-span-6 flex gap-3 items-center">
          <button disabled={loading} className="btn btn-primary" data-testid="source-submit"><Plus size={14}/> {editingId ? "Update source" : "Add source"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="text-[12px] font-mono text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid="source-cancel">Cancel</button>}
        </div>
      </form>

      <div className="io-card-el overflow-hidden" data-testid="source-list">
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--io-bg-2)] border-b border-[var(--io-border)]">
            <tr className="text-left">
              {["Name","Type","Region","Rel.","Freq","Active","Actions"].map((h) => (
                <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[var(--io-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-[var(--io-border)] hover:bg-[var(--io-bg-2)]/50" data-testid={`source-row-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="font-sans font-medium text-[var(--io-text)]">{s.name}</div>
                  <a href={s.url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-[#3B82F6] hover:text-[var(--io-gold)] break-all">{s.url}</a>
                  {s.copyright_notes && <div className="text-[11px] text-[var(--io-muted)] italic mt-1">{s.copyright_notes}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-[var(--io-text-2)] uppercase">{s.source_type}</td>
                <td className="px-4 py-3 text-[var(--io-text)]">{s.region}</td>
                <td className="px-4 py-3 text-[var(--io-gold)] font-mono">{s.reliability}/10</td>
                <td className="px-4 py-3 text-[var(--io-text-2)] font-mono">{s.fetch_frequency_hours}h</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(s)} className={`io-badge ${s.active ? "io-badge-green" : ""}`} data-testid={`toggle-active-${s.id}`}>{s.active ? "Active" : "Paused"}</button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => fetchNow(s.id)} disabled={ingesting === s.id} className="p-2 border border-[var(--io-border)] rounded-md hover:border-[var(--io-gold)] text-[var(--io-text-2)] hover:text-[var(--io-gold)] disabled:opacity-60" data-testid={`fetch-${s.id}`} title="Fetch now"><RefreshCw size={12} className={ingesting === s.id ? "animate-spin" : ""}/></button>
                    <button onClick={() => edit(s)} className="p-2 border border-[var(--io-border)] rounded-md hover:border-[var(--io-gold)] text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid={`edit-${s.id}`}><Edit2 size={12}/></button>
                    <button onClick={() => remove(s.id)} className="p-2 border border-transparent rounded-md text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`delete-${s.id}`}><Trash2 size={12}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
